"use client"

import { useMemo, useState, useEffect } from "react"
import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, DropdownSection } from "@heroui/dropdown"
import { Button } from "@heroui/button"
import { ScrollShadow } from "@heroui/scroll-shadow"
import { useInView } from "react-intersection-observer"
import { FaRegBell, FaBell } from "react-icons/fa"
import { MdOutlineCampaign, MdOutlineComment, MdOutlineMarkEmailRead, MdOutlineMessage, MdOutlineReportGmailerrorred, MdCheckCircle } from "react-icons/md"
import { useStoreNotification } from "@/app/store/slices/notification/hook"
import { NotificationItem, NotificationType } from "@/app/store/slices/notification/slice"
import clsx from "clsx"

const TYPE_META: Record<NotificationType, { label: string, color: string, icon: JSX.Element }> = {
  advertising: { label: "Publicité", color: "text-warning", icon: <MdOutlineCampaign size={18} /> },
  comment: { label: "Commentaire", color: "text-primary", icon: <MdOutlineComment size={18} /> },
  reporting: { label: "Signalement", color: "text-danger", icon: <MdOutlineReportGmailerrorred size={18} /> },
  message: { label: "Message", color: "text-secondary", icon: <MdOutlineMessage size={18} /> },
  demand: { label: "Demande", color: "text-success", icon: <MdOutlineMarkEmailRead size={18} /> }
}

const timeAgo = (date: string) => {
  return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function NotificationRow({ item, onRead }: { item: NotificationItem, onRead: () => void }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.8 })
  const meta = TYPE_META[item.type] || TYPE_META.message

  // Marquer comme lu automatiquement au scroll après 1 seconde
  useEffect(() => {
    if (inView && !item.read) {
      const timer = setTimeout(() => onRead(), 1000)
      return () => clearTimeout(timer)
    }
  }, [inView, item.read, onRead])

  const content = item.type === "advertising" ? item.value.subtitle
    : item.type === "comment" ? `"${item.value.content}"`
      : item.type === "reporting" ? "Un contenu a été signalé."
        : item.value.content || "Nouveau contenu"

  return (
    <div ref={ref} className="flex gap-3 py-3 w-full group">
      {/* Icone Type */}
      <div className={clsx("mt-1 flex-shrink-0 p-2 rounded-full bg-default-100/50 h-fit", meta.color)}>
        {meta.icon}
      </div>

      <div className="flex flex-col flex-grow gap-0.5 min-w-0">
        <div className="flex justify-between items-start">
          <span className="text-xs font-bold text-default-600">{meta.label}</span>
          <span className="text-[10px] text-default-400">{timeAgo(new Date().toISOString())}</span>
        </div>

        <p className={clsx("text-sm line-clamp-2", item.read ? "text-default-500" : "text-foreground font-medium")}>
          {content}
        </p>

        {item.type === "advertising" && item.value.link && (
          <a href={item.value.link} className="text-xs text-primary hover:underline mt-1 block" target="_blank">
            Voir le lien externe
          </a>
        )}
      </div>

      {/* Indicateur Non-lu */}
      {!item.read && (
        <div className="flex flex-col justify-center items-center pl-1">
          <div className="w-2 h-2 rounded-full bg-primary shadow-lg shadow-primary/50 animate-pulse" />
        </div>
      )}
    </div>
  )
}

export default function NotificationView({ expand = false }: { expand?: boolean }) {
  const { notifications, unreadCount, markRead } = useStoreNotification()
  const [isOpen, setIsOpen] = useState(false)

  const items = useMemo(() => notifications.slice().reverse(), [notifications])

  return (
    <Dropdown
      placement={expand ? "right-end" : "top-start"}
      offset={10}
      onOpenChange={setIsOpen}
      shouldFlip
      classNames={{
        content: "p-0 min-w-[340px] border border-default-100 shadow-2xl bg-background/80 backdrop-blur-xl"
      }}
    >
      <DropdownTrigger>
        <div className="relative inline-flex"> {/* Wrapper pour positionner le badge correctement */}
          <Button
            isIconOnly
            variant={isOpen ? "flat" : "light"}
            radius="lg"
            className="overflow-visible" // Important pour voir le badge
            aria-label="Notifications"
          >
            {unreadCount > 0 ? <FaBell size={20} className="text-foreground" /> : <FaRegBell size={20} />}

            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-danger-500 border-2 border-background"></span>
              </span>
            )}
          </Button>
        </div>
      </DropdownTrigger>

      <DropdownMenu
        aria-label="Notifications"
        variant="faded"
        closeOnSelect={false}
        className="p-0"
        itemClasses={{
          base: "gap-4 data-[hover=true]:bg-default-100/50 py-0 px-3 rounded-none border-b border-default-100/50 last:border-b-0",
        }}
      >
        <DropdownSection
          title={"Notifications"}
          classNames={{ group: "p-0" }}
        >
          <DropdownItem key="list" isReadOnly className="cursor-default hover:!bg-transparent p-0">
            <ScrollShadow className="max-h-[400px] w-full">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-default-400">
                  <div className="p-3 bg-default-100 rounded-full">
                    <FaRegBell size={24} />
                  </div>
                  <p className="text-sm">Aucune notification</p>
                </div>
              ) : (
                <div>
                  {items.map((item) => (
                    <div key={`${item.type}-${item.value._id}`} className="px-3 hover:bg-default-100/40 transition-colors border-b border-default-100/50 last:border-none">
                      <NotificationRow
                        item={item}
                        onRead={() => markRead(item.type, item.value._id)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </ScrollShadow>
          </DropdownItem>
        </DropdownSection>

        {items.length > 0 ? (
          <DropdownSection classNames={{ group: "p-0" }}>
            <DropdownItem key="footer" isReadOnly className="p-0 text-center">
              <div className="p-2 bg-default-50/50 border-t border-default-200/50">
                <Button size="sm" variant="light" className="w-full text-xs text-default-500">
                  Tout marquer comme lu
                </Button>
              </div>
            </DropdownItem>
          </DropdownSection>
        ) : <></>}
      </DropdownMenu>
    </Dropdown>
  )
}